import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Network, ArrowUp, ArrowDown, Hash, Eye, EyeOff, AlertTriangle, CheckSquare, Square } from "lucide-react";
import { Field, inp, VisibilityBadge } from "@/components/admin";

export function CurriculumDialog({ courses, batches, onDone }) {
  const [open, setOpen] = useState(false);
  const [courseId, setCourseId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!courseId) return;
    setBusy(true);
    try {
      const qs = new URLSearchParams({ ...(batchId ? { batch_id: batchId } : {}) }).toString();
      const { data } = await api.get(`/admin/courses/${courseId}/curriculum${qs ? "?" + qs : ""}`);
      setData(data);
      setSelected(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (open && courseId) load(); /* eslint-disable-next-line */ }, [open, courseId, batchId]);

  const move = async (cls, dir) => {
    // dir = -1 (up) or +1 (down); just swap day_number with adjacent
    const allBatches = data?.batches || {};
    const list = allBatches[cls.batch_id || "_no_batch"] || [];
    const idx = list.findIndex((c) => c.id === cls.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const other = list[swapIdx];
    const items = [
      { id: cls.id, day_number: other.day_number || (swapIdx + 1) },
      { id: other.id, day_number: cls.day_number || (idx + 1) },
    ];
    try {
      await api.post("/admin/classes/reorder", { items });
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const autoNumber = async () => {
    if (!courseId) return;
    if (!window.confirm("Re-number ALL classes by scheduled time? day_number will be reset to 1, 2, 3…")) return;
    try {
      const { data } = await api.post("/admin/classes/auto-number", { course_id: courseId, batch_id: batchId || null });
      toast.success(`✅ Re-numbered ${data.renumbered} classes`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulkVisibility = async (visibility) => {
    if (!selected.size) { toast.error("Select kuch classes pehle"); return; }
    if (!window.confirm(`Set visibility = "${visibility}" for ${selected.size} classes?`)) return;
    try {
      const { data } = await api.post("/admin/classes/bulk-visibility", {
        class_ids: Array.from(selected), visibility,
      });
      toast.success(`✅ Updated ${data.updated} classes → ${visibility}`);
      onDone?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = (list) => {
    const ids = list.map((c) => c.id);
    const allSelected = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allSelected) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    setSelected(next);
  };

  const courseBatches = batches.filter((b) => b.course_id === courseId);
  const batchName = (bid) => batches.find((b) => b.id === bid)?.name || "(no batch)";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-curriculum">
          <Network className="w-4 h-4" /> Curriculum
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Network className="w-5 h-5 text-fuchsia-300" /> Smart Course Flow / Curriculum</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Reorder lectures, detect numbering gaps, bulk publish/hide, auto-number per course or batch.</div>
        </DialogHeader>

        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Field label="Course">
            <select value={courseId} onChange={(e) => { setCourseId(e.target.value); setBatchId(""); }} className={inp} data-testid="curriculum-course">
              <option value="">Select course…</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <Field label="Filter by batch (optional)">
            <select value={batchId} onChange={(e) => setBatchId(e.target.value)} className={inp} disabled={!courseId}>
              <option value="">All batches</option>
              {courseBatches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <div className="flex items-end">
            <button onClick={autoNumber} disabled={!courseId || busy} className="btn-ghost text-sm w-full justify-center disabled:opacity-50">
              <Hash className="w-4 h-4" /> Auto-number by date
            </button>
          </div>
        </div>

        {data && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2">
                <div className="text-[10px] uppercase text-white/50">Total</div>
                <div className="text-xl font-bold">{data.total}</div>
              </div>
              {["published", "draft", "scheduled", "hidden"].map((k) => (
                <div key={k} className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2">
                  <div className="text-[10px] uppercase text-white/50">{k}</div>
                  <div className="text-xl font-bold">{data.visibility_counts[k] || 0}</div>
                </div>
              ))}
            </div>

            {data.missing_lecture_numbers?.length > 0 && (
              <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-3 mb-3 text-sm">
                <div className="flex items-center gap-2 text-red-300 font-semibold"><AlertTriangle className="w-4 h-4" /> Missing lecture numbers detected</div>
                <div className="text-xs text-red-200 mt-1">#{data.missing_lecture_numbers.join(", #")} — consider adding these or using Auto-number to fix gaps.</div>
              </div>
            )}

            {/* Bulk actions toolbar */}
            {selected.size > 0 && (
              <div className="sticky top-0 z-10 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/40 p-2.5 mb-3 flex items-center gap-2 flex-wrap" data-testid="bulk-toolbar">
                <div className="text-xs text-fuchsia-200 font-semibold">{selected.size} selected</div>
                <div className="flex gap-1.5 ml-auto flex-wrap">
                  <button onClick={() => bulkVisibility("published")} className="btn-ghost !py-1 text-[11px]"><Eye className="w-3 h-3" /> Publish</button>
                  <button onClick={() => bulkVisibility("draft")} className="btn-ghost !py-1 text-[11px]">📝 Draft</button>
                  <button onClick={() => bulkVisibility("hidden")} className="btn-ghost !py-1 text-[11px]"><EyeOff className="w-3 h-3" /> Hide</button>
                  <button onClick={() => bulkVisibility("archived")} className="btn-ghost !py-1 text-[11px]">📦 Archive</button>
                  <button onClick={() => setSelected(new Set())} className="btn-ghost !py-1 text-[11px]">Clear</button>
                </div>
              </div>
            )}

            {/* Per-batch trees */}
            {Object.entries(data.batches).map(([bid, list]) => (
              <div key={bid} className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold">{batchName(bid)}</div>
                  <div className="text-[10px] text-white/40">{list.length} classes</div>
                  <button onClick={() => toggleAll(list)} className="text-[10px] text-white/60 hover:text-fuchsia-300 ml-auto">
                    {list.every((c) => selected.has(c.id)) ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="rounded-xl border border-white/10 divide-y divide-white/5">
                  {list.map((c, idx) => (
                    <div key={c.id} className="flex items-center gap-2 p-2 hover:bg-white/[0.02]" data-testid={`curriculum-row-${c.id}`}>
                      <button onClick={() => toggle(c.id)} className="shrink-0 p-1 text-fuchsia-300 hover:bg-fuchsia-500/15 rounded">
                        {selected.has(c.id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-white/40" />}
                      </button>
                      <div className="w-7 text-center text-xs font-mono text-white/50">D{c.day_number || idx + 1}</div>
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button onClick={() => move(c, -1)} disabled={idx === 0} className="text-white/40 hover:text-fuchsia-300 disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                        <button onClick={() => move(c, +1)} disabled={idx === list.length - 1} className="text-white/40 hover:text-fuchsia-300 disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.title}</div>
                        <div className="text-[10px] text-white/50">{new Date(c.scheduled_at).toLocaleString()} • {c.duration_minutes}min • {c.teacher_name || "no teacher"}</div>
                      </div>
                      <VisibilityBadge visibility={c.visibility} />
                    </div>
                  ))}
                  {list.length === 0 && <div className="px-3 py-4 text-xs text-white/40">No classes</div>}
                </div>
              </div>
            ))}
          </>
        )}

        {!data && !busy && courseId && <div className="text-sm text-white/40 text-center py-8">Loading curriculum…</div>}
        {!courseId && <div className="text-sm text-white/40 text-center py-12">Course select karke curriculum load karein.</div>}
      </DialogContent>
    </Dialog>
  );
}

export default CurriculumDialog;
